import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { mlService } from '../services/api';
import { useToast } from './Toast';
import { useMontoTexto } from './Monto';
import { useAuthStore } from '../store/auth';
import { aplicarCaptura, hoyLocal } from '../utils/captura';
import { useDialog } from './Dialog';
import { FOCO } from '../utils/a11y';

export default function QuickAdd() {
  const fmtMonto = useMontoTexto();
  const esPro = useAuthStore(s => s.user?.plan) === 'pro';
  const [open, setOpen]                   = useState(false);
  const [tipo, setTipo]                   = useState('gasto');
  const [monto, setMonto]                 = useState('');
  const [desc, setDesc]                   = useState('');
  const [fecha, setFecha]                 = useState(hoyLocal);
  const [texto, setTexto]                 = useState('');
  const [capturando, setCapturando]       = useState(false);
  const [capturaHint, setCapturaHint]     = useState('');
  const [categoriaId, setCategoriaId]     = useState('');
  const [categorias, setCategorias]       = useState([]);
  const [sugerida, setSugerida]           = useState(null);
  const [saving, setSaving]               = useState(false);
  const [divisa, setDivisa]               = useState('MXN');
  const [tasaCambio, setTasaCambio]       = useState(null);
  const { addToast }                      = useToast();
  const montoRef                          = useRef(null);
  const predictTimer                      = useRef(null);
  const panelRef                          = useRef(null);
  const cerrar                            = useCallback(() => setOpen(false), []);
  // Foco al monto, Tab atrapado, Escape y foco de vuelta al botón +
  useDialog(panelRef, open, cerrar);

  const DIVISAS = [
    { id: 'MXN', nombre: '🇲🇽 MXN' },
    { id: 'USD', nombre: '🇺🇸 USD' },
    { id: 'EUR', nombre: '🇪🇺 EUR' },
    { id: 'ARS', nombre: '🇦🇷 ARS' },
    { id: 'COP', nombre: '🇨🇴 COP' },
  ];

  // Keyboard shortcut: Alt+N
  useEffect(() => {
    const handler = (e) => {
      if ((e.altKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setFecha(hoyLocal());
      api.get('/categorias').then(r => setCategorias(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } else {
      setMonto(''); setDesc(''); setTipo('gasto'); setCategoriaId(''); setSugerida(null);
      setDivisa('MXN'); setTasaCambio(null);
      setFecha(hoyLocal()); setTexto(''); setCapturaHint(''); setCapturando(false);
    }
  }, [open]);

  // Tipo de cambio del día cuando se elige una divisa distinta a MXN
  useEffect(() => {
    if (divisa === 'MXN') { setTasaCambio(null); return; }
    let cancelado = false;
    api.get('/banxico/tipo-cambio', { params: { moneda: divisa } })
      .then(r => { if (!cancelado) setTasaCambio(r.data?.tasa ?? null); })
      .catch(() => { if (!cancelado) setTasaCambio(null); });
    return () => { cancelado = true; };
  }, [divisa]);

  // Debounced ML prediction on description change
  useEffect(() => {
    clearTimeout(predictTimer.current);
    if (!open || desc.length < 3) { setSugerida(null); return; }
    predictTimer.current = setTimeout(async () => {
      try {
        const res = await mlService.predictCategory(desc, tipo);
        setSugerida(res.data?.categoria || null);
      } catch { setSugerida(null); }
    }, 600);
    return () => clearTimeout(predictTimer.current);
  }, [desc, tipo, open]);

  const aplicarSugerida = () => {
    if (!sugerida) return;
    const cat = categorias.find(c => c.nombre?.toLowerCase() === sugerida.toLowerCase())
              || categorias.find(c => c.nombre?.toLowerCase().includes(sugerida.toLowerCase()));
    if (cat) { setCategoriaId(String(cat.id)); setSugerida(null); }
  };

  // Captura en lenguaje natural (solo Pro): el backend parsea con reglas y usa IA
  // solo como respaldo validado. Nunca guarda: solo llena el formulario.
  const llenarDesdeTexto = async () => {
    const t = texto.trim();
    if (!t || capturando) return;
    setCapturando(true); setCapturaHint('');
    try {
      const res = await api.post('/asistente/captura', { texto: t });
      const form = aplicarCaptura(res.data, { tipo, monto, desc, fecha });
      setTipo(form.tipo); setMonto(form.monto); setDesc(form.desc); setFecha(form.fecha);
      const s = res.data?.sugerencia;
      if (res.data?.ia?.mensaje) setCapturaHint(res.data.ia.mensaje);
      else if (!s?.monto) setCapturaHint('No encontré el monto; complétalo abajo.');
    } catch (err) {
      const body = err.response?.data;   // el backend envuelve errores en {data:{error}}
      setCapturaHint(body?.data?.error || body?.error || 'No se pudo interpretar; llena el formulario a mano.');
    } finally { setCapturando(false); }
  };

  const guardar = async (e) => {
    e?.preventDefault();
    const m = parseFloat(monto);
    if (!m || m <= 0) { addToast('Ingresa un monto válido', 'error'); return; }
    if (!desc.trim()) { addToast('Escribe una descripción', 'error'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) { addToast('Elige una fecha válida', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/transacciones', {
        tipo, monto: m, descripcion: desc.trim(),
        fecha_transaccion: fecha,
        estado: 'completada',
        divisa,
        ...(categoriaId ? { categoria_id: parseInt(categoriaId) } : {}),
      });
      // monto-ok: toast transitorio de una accion recien hecha
      addToast(`${tipo === 'ingreso' ? 'Ingreso' : 'Gasto'} de $${m.toLocaleString('es-MX', { minimumFractionDigits: 2 })} registrado`, 'success');
      setOpen(false);
    } catch (err) {
      addToast(err.response?.data?.error || 'Error al guardar', 'error');
    } finally { setSaving(false); }
  };

  return (
    <>
      {/* FAB */}
      <button type="button" onClick={() => setOpen(true)} title="Nuevo movimiento (Alt+N)"
        aria-label="Nuevo movimiento (Alt+N)" aria-keyshortcuts="Alt+N"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all active:scale-90 select-none ${FOCO} focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900`}
        style={{ fontSize: 28 }}>
        <span aria-hidden="true">+</span>
      </button>

      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-[9990] flex items-end sm:items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <form onSubmit={guardar} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="quickadd-titulo"
            className="relative bg-white dark:bg-slate-900 w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 z-10 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 id="quickadd-titulo" className="font-bold text-gray-900 dark:text-white text-lg">Nuevo movimiento</h3>
              <button type="button" onClick={() => setOpen(false)} aria-label="Cerrar"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none px-1">✕</button>
            </div>

            {/* Captura en lenguaje natural (Pro) */}
            {esPro && (
              <div className="mb-4">
                <div className="flex gap-2">
                  <input value={texto} onChange={e => setTexto(e.target.value)} maxLength={200}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); llenarDesdeTexto(); } }}
                    placeholder="Escríbelo como lo dirías…" aria-label="Escríbelo como lo dirías"
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 outline-none transition text-sm"
                  />
                  <button type="button" onClick={llenarDesdeTexto} disabled={capturando || !texto.trim()}
                    className="px-3 py-2 rounded-xl text-sm font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition disabled:opacity-50">
                    {capturando ? '...' : 'Llenar'}
                  </button>
                </div>
                {capturaHint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{capturaHint}</p>}
              </div>
            )}

            {/* Tipo */}
            <div className="flex gap-2 mb-4">
              {[['gasto','🛒 Gasto'], ['ingreso','💰 Ingreso']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setTipo(v)}
                  className={`flex-1 py-2.5 rounded-xl font-semibold text-sm border-2 transition ${
                    tipo === v
                      ? v === 'gasto'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300'
                        : 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300'
                      : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                  }`}>{l}</button>
              ))}
            </div>

            {/* Monto + Divisa */}
            <div className="flex gap-2 mb-1">
              <div className="relative flex-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-bold text-lg">$</span>
                <input ref={montoRef} data-autofocus type="number" aria-label="Monto" value={monto} onChange={e => setMonto(e.target.value)}
                  placeholder="0.00" min="0.01" step="0.01"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition text-xl font-bold"
                />
              </div>
              <select aria-label="Divisa" value={divisa} onChange={e => setDivisa(e.target.value)}
                className="px-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition text-sm font-semibold">
                {DIVISAS.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
              </select>
            </div>
            {divisa !== 'MXN' && (
              <p className="text-xs text-gray-400 mb-3">
                {tasaCambio
                  ? `≈ ${fmtMonto((parseFloat(monto) || 0) * tasaCambio, 2)} MXN (tipo de cambio: ${tasaCambio})`
                  : 'Consultando tipo de cambio...'}
              </p>
            )}
            {divisa === 'MXN' && <div className="mb-3" />}

            {/* Descripción */}
            <input value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="Descripción..." aria-label="Descripción"
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition mb-2"
            />

            {/* ML suggestion chip */}
            {sugerida && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">🤖 IA sugiere:</span>
                <button type="button" onClick={aplicarSugerida}
                  className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-full px-3 py-1 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition">
                  {sugerida} ✓
                </button>
                <button type="button" onClick={() => setSugerida(null)} aria-label="Descartar sugerencia"
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">✕</button>
              </div>
            )}

            {/* Fecha */}
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              aria-label="Fecha"
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition text-sm mb-2"
            />

            {/* Categoría */}
            <select aria-label="Categoría" value={categoriaId} onChange={e => { setCategoriaId(e.target.value); setSugerida(null); }}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-blue-600 dark:focus:border-blue-400 outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:focus-visible:ring-blue-400 transition text-sm mb-4">
              <option value="">Categoría (opcional)</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>

            <button type="submit" disabled={saving}
              className={`w-full py-3 rounded-xl font-bold text-white transition active:scale-95 ${
                tipo === 'gasto' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
              } disabled:opacity-50`}>
              {saving ? 'Guardando...' : `Registrar ${tipo}`}
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">Alt+N para abrir · Esc para cerrar</p>
          </form>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.22s ease-out; }
        @media (min-width: 640px) {
          .animate-slide-up {
            animation: scale-in 0.18s ease-out;
          }
          @keyframes scale-in {
            from { transform: scale(0.95); opacity: 0; }
            to   { transform: scale(1); opacity: 1; }
          }
        }
      `}</style>
    </>
  );
}
